import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../utils/apiClient';
import { consumeSessionExpiredNotice } from '../utils/sessionExpiry';
import { Box } from '@mui/material';
import { AuthSplitLayout, HeroPanel, SignInForm, SsoProvider } from '../auth';
import { PrivacyNoticeLink } from './privacy/PrivacyNoticeLink';

interface WorkspacePublic {
  requireSso: boolean;
  workspaceDisplayName: string;
  providers: SsoProvider[];
}

interface LegacyProvider {
  id: string;
  name: string;
  type: string;
  isEnabled: boolean;
  isConfigured: boolean;
}

const DEFAULT_WORKSPACE: WorkspacePublic = {
  requireSso: false,
  workspaceDisplayName: 'Teamlane',
  providers: [],
};

interface LoginPageProps {
  /** `/login/owner`: the Owner password form, for when single sign-on is unavailable. */
  ownerSignIn?: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ ownerSignIn = false }) => {
  const getNoAuth = useCallback((): string | null => null, []);
  const apiClient = useApiClient(getNoAuth);
  const [workspace, setWorkspace] = useState<WorkspacePublic>(DEFAULT_WORKSPACE);
  // Show a one-shot notice if we landed here because an authenticated session
  // expired. Consumed in a ref-guarded effect (not a useState initializer) so
  // StrictMode's double-invoke can't read-and-clear the flag twice and lose it.
  const [notice, setNotice] = useState<string | null>(null);
  const noticeConsumed = useRef(false);
  useEffect(() => {
    if (noticeConsumed.current) return;
    noticeConsumed.current = true;
    if (consumeSessionExpiredNotice()) {
      setNotice('Your session has expired. Please sign in again.');
    }
  }, []);

  useEffect(() => {
    void apiClient
      .get<WorkspacePublic>('/api/auth/workspace-public')
      .then((data) => setWorkspace(data))
      .catch(() => {
        // Fallback for environments where Phase 1 backend hasn't deployed.
        void apiClient
          .get<LegacyProvider[]>('/api/auth/sso/providers')
          .then((providers) =>
            setWorkspace((prev) => ({
              ...prev,
              providers: providers
                .filter((p) => p.isEnabled && p.isConfigured)
                .map(({ id, name, type }) => ({ id, name, type })),
            })),
          )
          .catch(() => {});
      });
  }, [apiClient]);

  return (
    <AuthSplitLayout hero={<HeroPanel />}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <SignInForm
          ssoOnly={workspace.requireSso}
          providers={workspace.providers}
          notice={notice ?? undefined}
          workspaceName={workspace.workspaceDisplayName}
          ownerSignIn={ownerSignIn}
        />
        <PrivacyNoticeLink sx={{ typography: 'caption', color: 'text.secondary' }} />
      </Box>
    </AuthSplitLayout>
  );
};

export default LoginPage;
